/**
 * Where a disclosure panel goes relative to its trigger, when the space it
 * needs is bounded by a scroll container (M1-40).
 *
 * ⚠️ **Geometry in, choice out — deliberately, and this is the AC's own
 * requirement rather than a style preference.** jsdom composites nothing and
 * reports every width and height as zero, so a placement decision entangled
 * with the DOM cannot be tested at all: every rect would be `0,0,0,0` and every
 * assertion would pass for the wrong reason. Keeping the decision a function of
 * six numbers means the boundary — the exact pixel where "fits below" becomes
 * "does not" — is checkable in both directions with no layout at all, and the
 * component's job shrinks to measuring honestly.
 *
 * The measuring half lives in {@link nearestScrollableAncestor}, which is the
 * only part that needs a real browser, and is exercised by the browser pass
 * rather than pretended at in jsdom.
 */

export type TooltipPlacement = 'below' | 'above';

export interface PlacementGeometry {
  /** Viewport-relative top of the trigger, from `getBoundingClientRect()`. */
  triggerTop: number;
  /** Viewport-relative bottom of the trigger. */
  triggerBottom: number;
  /** Viewport-relative top of the clipping scroll container. */
  containerTop: number;
  /** Viewport-relative bottom of the clipping scroll container. */
  containerBottom: number;
  /** The panel's own height, measured after it has rendered once. */
  panelHeight: number;
  /**
   * The visual offset between trigger and panel. It is PADDING on the
   * positioned wrapper rather than margin (see `TechnicianTooltip` for the
   * WCAG 1.4.13 reason), so it occupies space on whichever side the panel
   * lands and has to be counted on both.
   */
  gap: number;
}

/**
 * Prefers below, flips above only when below genuinely does not fit and above
 * does better.
 *
 * <para>Below is the default because it is what a reader expects of a
 * disclosure hanging off a header, and because flipping is disorienting when it
 * is not necessary — a panel that jumps sides as content loads is worse than
 * one that is consistently below. So the rule is not "pick the roomier side",
 * which would flip on a one-pixel difference; it is "stay below unless below is
 * actually too small".</para>
 *
 * <para>When NEITHER side fits — a container shorter than the panel, which the
 * week board's 246px scroller genuinely is against a 250px panel — it picks the
 * side with more room. That is a real state, not a defensive branch: it cannot
 * show everything, so it shows the most it can, and the scroll container
 * (focusable since M1-27) still reaches the rest.</para>
 */
export function decideTooltipPlacement({
  triggerTop,
  triggerBottom,
  containerTop,
  containerBottom,
  panelHeight,
  gap,
}: PlacementGeometry): TooltipPlacement {
  const spaceBelow = containerBottom - triggerBottom - gap;
  const spaceAbove = triggerTop - containerTop - gap;

  if (panelHeight <= spaceBelow) {
    return 'below';
  }

  if (panelHeight <= spaceAbove) {
    return 'above';
  }

  // Neither fits. Show the most of it that can be shown.
  return spaceAbove > spaceBelow ? 'above' : 'below';
}

/**
 * The nearest ancestor that actually clips — the thing the panel has to fit
 * inside, which is not necessarily the offset parent.
 *
 * ⚠️ Checks the COMPUTED overflow, not a class name or a data attribute. The
 * week board's container is `overflow-x-auto`, and Tailwind's rule sets only
 * `overflow-x` — the browser then computes `overflow-y` to `auto` as well,
 * because a box cannot clip on one axis and be visible on the other. So the
 * container that clips this panel vertically never says "y" anywhere in the
 * source, and anything matching on the authored class would miss it.
 *
 * Returns null when nothing clips, which is a real answer: the panel is then
 * bounded by the viewport and the caller can fall back to it.
 */
export function nearestScrollableAncestor(from: Element | null): Element | null {
  let node = from?.parentElement ?? null;
  while (node) {
    const style = getComputedStyle(node);
    const clips = [style.overflowY, style.overflowX].some(
      (value) => value === 'auto' || value === 'scroll' || value === 'hidden',
    );
    if (clips) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}
